import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useSearchParams } from 'react-router-dom';
import { alertsAPI, camerasAPI, zonesAPI, sitesAPI, getThumbnailUrl, getAlertImageUrl, getImageUrl } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import { useSocketStore } from '../stores/socketStore';
import { formatDateTime, getActiveTimezoneInfo, currentMonthToDateRange } from '../utils/formatDateTime';
import TimezonePill from '../components/TimezonePill';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ImageModal, { AlertThumbnail } from '../components/ImageModal';
import {
  FunnelIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  FireIcon,
  BellAlertIcon,
  PhotoIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  TruckIcon,
  ArrowDownTrayIcon,
  NoSymbolIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const severityOptions = ['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const statusOptions = ['ALL', 'ACTIVE', 'ACKNOWLEDGED', 'RESOLVED', 'FALSE_POSITIVE'];
const categoryOptions = ['ALL', 'PPE', 'FIRE', 'ZONE', 'VEHICLE', 'EQUIPMENT', 'BEHAVIOR', 'HOUSEKEEPING', 'ENVIRONMENTAL', 'COMPLIANCE', 'PERMIT'];
const quickFilters = [
  { id: 'active-critical', label: 'Active Critical', values: { severity: 'CRITICAL', status: 'ACTIVE', category: 'ALL' } },
  { id: 'fire', label: 'Fire/Smoke', values: { severity: 'ALL', status: 'ACTIVE', category: 'FIRE' } },
  { id: 'ppe', label: 'PPE', values: { severity: 'ALL', status: 'ACTIVE', category: 'PPE' } },
  { id: 'zone', label: 'Zone Breach', values: { severity: 'ALL', status: 'ACTIVE', category: 'ZONE' } },
  { id: 'false-positive', label: 'False Positives', values: { severity: 'ALL', status: 'FALSE_POSITIVE', category: 'ALL' } },
];

/**
 * Format violation display for alerts with multiple violations
 * @param {Object} alert - The alert object
 * @returns {Object} - { primary: string, secondary: string|null }
 */
const formatViolations = (alert) => {
  // Check if this is a grouped violation alert
  const violations = alert.metadata?.violations || alert.violations;
  let violationCount = alert.metadata?.violationCount || alert.violationCount;

  if (violations && Array.isArray(violations) && violations.length > 0) {
    if (violations.length > 1) {
      // Multiple violations - show count and list
      const formattedList = violations
        .filter(v => v)  // Filter out empty/null values
        .map(v => v.replace(/_/g, ' ').replace(/MISSING /g, ''))
        .join(', ');
      // Use violations.length as fallback if violationCount is undefined, null, or 0
      const count = violationCount && violationCount > 0 ? violationCount : violations.length;
      return {
        primary: `${count} Violations`,
        secondary: formattedList,
      };
    } else if (violations.length === 1) {
      // Single violation from array - format and display it
      const violationName = violations[0].replace(/_/g, ' ').replace(/MISSING /g, '');
      return {
        primary: violationName,
        secondary: null,
      };
    }
  }

  // Handle vehicle proximity alerts specially
  if (alert.type === 'VEHICLE_PROXIMITY') {
    const distance = alert.metadata?.estimatedDistance || alert.estimatedDistance;
    const vehicleType = alert.metadata?.vehicleType || alert.vehicleType || '';
    const proximityLevel = alert.metadata?.proximityLevel || alert.proximityLevel || '';

    const primary = vehicleType
      ? `${vehicleType} ${proximityLevel}`.replace(/_/g, ' ')
      : alert.subType?.replace(/_/g, ' ') || 'Vehicle Nearby';

    const secondary = distance ? `~${distance}m distance` : null;

    return { primary, secondary };
  }

  // Fallback: use standard subType
  return {
    primary: alert.subType?.replace(/_/g, ' ') || 'Alert',
    secondary: null,
  };
};

const formatConfidence = (alert) => {
  const raw = alert.confidence ?? alert.metadata?.confidence ?? alert.metadata?.maxConfidence;
  const value = Number(raw);
  if (!Number.isFinite(value)) return '-';
  return `${Math.round((value <= 1 ? value : value / 100) * 100)}%`;
};

export default function AlertsPage() {
  const [searchParams] = useSearchParams();
  // Seed the date filter to the current month-to-date in the site's timezone, so
  // a fresh load / login shows "1st of month → today" (e.g. Jun 1 → Jun 19, and
  // Jun 1 → Jun 20 the next day). Computed once on mount.
  const [filters, setFilters] = useState(() => {
    const { startDate, endDate } = currentMonthToDateRange();
    return {
      severity: 'ALL',
      status: searchParams.get('status') || 'ACTIVE',
      category: searchParams.get('category') || 'ALL',
      cameraId: '',
      zoneId: searchParams.get('zoneId') || '',
      siteId: '',
      startDate,
      endDate,
      page: 1,
    };
  });
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [viewingAlert, setViewingAlert] = useState(null); // For image modal with metadata
  // When the lightbox pages across a list-page boundary we flip filters.page and
  // remember which end of the freshly-loaded page to open: 'first' (stepped
  // forward into the next page) or 'last' (stepped back into the previous page).
  const [pendingEdgeNav, setPendingEdgeNav] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Deep-link from email "View Alert Details" (/alerts?alertId=<id>): fetch that
  // specific alert by ID and open its detail modal. We fetch directly (not from the
  // filtered list) so it works even when the alert is resolved/old and wouldn't show
  // under the default ACTIVE filter. Runs once per alertId value.
  const deepLinkAlertId = searchParams.get('alertId');
  useEffect(() => {
    if (!deepLinkAlertId) return;
    let cancelled = false;
    alertsAPI.getAlert(deepLinkAlertId)
      .then(res => { if (!cancelled && res?.data) setViewingAlert(res.data); })
      .catch(() => { /* alert not found / deleted — silently ignore, page still loads */ });
    return () => { cancelled = true; };
  }, [deepLinkAlertId]);

  const { selectedSite } = useSiteStore();
  const { latestAlert } = useSocketStore();
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['alerts', selectedSite?.id, filters],
    queryFn: () => alertsAPI.getAlerts({
      siteId: filters.siteId || selectedSite?.id,
      severity: filters.severity !== 'ALL' ? filters.severity : undefined,
      status: filters.status !== 'ALL' ? filters.status : undefined,
      category: filters.category !== 'ALL' ? filters.category : undefined,
      cameraId: filters.cameraId || undefined,
      zoneId: filters.zoneId || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      // Site timezone so the backend bounds the day filter to match what's shown.
      tz: getActiveTimezoneInfo().timezone,
      page: filters.page,
      limit: 20,
    }),
    // Use WebSocket for real-time updates instead of polling
    // Only refetch every 60 seconds as a fallback
    refetchInterval: 60000,
    // Disable retries on rate limit errors
    retry: (failureCount, error) => {
      if (error?.response?.status === 429) return false;
      return failureCount < 2;
    },
    // Don't refetch on window focus if we have WebSocket
    refetchOnWindowFocus: false,
  });

  // Fetch cameras for filter dropdown
  const { data: camerasData } = useQuery({
    queryKey: ['cameras', selectedSite?.id],
    queryFn: () => camerasAPI.getCameras(selectedSite?.id),
    enabled: !!selectedSite?.id,
    staleTime: 300000,
  });
  const cameras = camerasData?.data?.data || [];

  // Fetch zones for filter dropdown
  const { data: zonesData } = useQuery({
    queryKey: ['zones', selectedSite?.id],
    queryFn: () => zonesAPI.getZones({ siteId: selectedSite?.id, isActive: true }),
    enabled: !!selectedSite?.id,
    staleTime: 300000,
  });
  const zones = (() => {
    const raw = zonesData?.data;
    return Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  })();

  // Fetch all sites for the location filter
  const { data: sitesData } = useQuery({
    queryKey: ['sites-list'],
    queryFn: () => sitesAPI.getSites(),
    staleTime: 300000,
  });
  const sites = (() => {
    const raw = sitesData?.data;
    return Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
  })();

  // Refetch when new alert comes in via WebSocket
  useEffect(() => {
    if (latestAlert && filters.status === 'ACTIVE') {
      // Invalidate to get the new alert in the list
      queryClient.invalidateQueries({ queryKey: ['alerts', selectedSite?.id] });
    }
  }, [latestAlert, selectedSite?.id, filters.status, queryClient]);

  // Handle rate limit errors gracefully
  useEffect(() => {
    if (error?.response?.status === 429) {
      const retryAfter = error.response.data?.retryAfter || 60;
      toast.error(`Rate limited. Retrying in ${retryAfter}s`, { id: 'rate-limit' });
    }
  }, [error]);

  // Optimistic update for acknowledge
  const acknowledgeMutation = useMutation({
    mutationFn: (alertId) => alertsAPI.acknowledgeAlert(alertId),
    // Optimistic update - immediately show as acknowledged
    onMutate: async (alertId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['alerts', selectedSite?.id, filters] });

      // Snapshot the previous value
      const previousAlerts = queryClient.getQueryData(['alerts', selectedSite?.id, filters]);

      // Optimistically update the cache
      queryClient.setQueryData(['alerts', selectedSite?.id, filters], (old) => {
        if (!old?.data?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            data: old.data.data.map(alert =>
              alert.id === alertId
                ? { ...alert, status: 'ACKNOWLEDGED' }
                : alert
            ),
          },
        };
      });

      return { previousAlerts };
    },
    onSuccess: () => {
      toast.success('Alert acknowledged');
    },
    onError: (error, alertId, context) => {
      // Rollback on error
      if (context?.previousAlerts) {
        queryClient.setQueryData(['alerts', selectedSite?.id, filters], context.previousAlerts);
      }
      if (error?.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment.');
      } else {
        toast.error('Failed to acknowledge alert');
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  // Optimistic update for resolve
  const resolveMutation = useMutation({
    mutationFn: ({ alertId, resolution }) => alertsAPI.resolveAlert(alertId, { resolution }),
    // Optimistic update - immediately show as resolved
    onMutate: async ({ alertId }) => {
      await queryClient.cancelQueries({ queryKey: ['alerts', selectedSite?.id, filters] });

      const previousAlerts = queryClient.getQueryData(['alerts', selectedSite?.id, filters]);

      queryClient.setQueryData(['alerts', selectedSite?.id, filters], (old) => {
        if (!old?.data?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            data: old.data.data.map(alert =>
              alert.id === alertId
                ? { ...alert, status: 'RESOLVED' }
                : alert
            ),
          },
        };
      });

      return { previousAlerts };
    },
    onSuccess: () => {
      toast.success('Alert resolved');
      setSelectedAlert(null);
    },
    onError: (error, variables, context) => {
      if (context?.previousAlerts) {
        queryClient.setQueryData(['alerts', selectedSite?.id, filters], context.previousAlerts);
      }
      if (error?.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment.');
      } else {
        toast.error('Failed to resolve alert');
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  // Bulk acknowledge mutation
  const bulkAcknowledgeMutation = useMutation({
    mutationFn: (alertIds) => alertsAPI.bulkAcknowledge(alertIds),
    onSuccess: (_, alertIds) => {
      toast.success(`${alertIds.length} alert${alertIds.length > 1 ? 's' : ''} acknowledged`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: () => {
      toast.error('Failed to bulk acknowledge');
    },
  });

  const bulkResolveMutation = useMutation({
    mutationFn: (alertIds) => alertsAPI.bulkResolve(alertIds, 'Bulk resolved'),
    onSuccess: (_, alertIds) => {
      toast.success(`${alertIds.length} alert${alertIds.length > 1 ? 's' : ''} resolved`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: () => {
      toast.error('Failed to bulk resolve');
    },
  });

  // "Resolve All Active" — resolves every open alert for the current site
  // in one call (server-side filter). Distinct from bulkResolve which works
  // off explicit selected IDs.
  const resolveAllMutation = useMutation({
    mutationFn: () => alertsAPI.resolveAll(selectedSite?.id ? { siteId: selectedSite.id } : {}),
    onSuccess: (res) => {
      const n = res?.data?.resolved ?? 0;
      toast.success(n > 0 ? `Resolved ${n} alert${n > 1 ? 's' : ''}` : 'No active alerts to resolve');
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['active-alert-count'] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.error || 'Failed to resolve all alerts');
    },
  });
  const [showResolveAllConfirm, setShowResolveAllConfirm] = useState(false);

  const bulkFalsePositiveMutation = useMutation({
    mutationFn: (alertIds) => alertsAPI.bulkFalsePositive(alertIds),
    onSuccess: (_, alertIds) => {
      toast.success(`${alertIds.length} alert${alertIds.length > 1 ? 's' : ''} marked as false positive`);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: () => {
      toast.error('Failed to mark as false positive');
    },
  });

  // Individual false positive mutation (per-alert, with optional reason)
  const falsePositiveMutation = useMutation({
    mutationFn: ({ alertId, reason }) => alertsAPI.markFalsePositive(alertId, { reason }),
    onMutate: async ({ alertId }) => {
      await queryClient.cancelQueries({ queryKey: ['alerts', selectedSite?.id, filters] });
      const previousAlerts = queryClient.getQueryData(['alerts', selectedSite?.id, filters]);
      queryClient.setQueryData(['alerts', selectedSite?.id, filters], (old) => {
        if (!old?.data?.data) return old;
        return {
          ...old,
          data: {
            ...old.data,
            data: old.data.data.map(a =>
              a.id === alertId ? { ...a, status: 'FALSE_POSITIVE' } : a
            ),
          },
        };
      });
      return { previousAlerts };
    },
    onSuccess: () => {
      toast.success('Alert marked as false positive — AI engine updated');
    },
    onError: (error, _, context) => {
      if (context?.previousAlerts) {
        queryClient.setQueryData(['alerts', selectedSite?.id, filters], context.previousAlerts);
      }
      toast.error('Failed to mark as false positive');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const actionable = (alerts || []).filter(a => a.status === 'ACTIVE' || a.status === 'ESCALATED');
    if (selectedIds.size === actionable.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(actionable.map(a => a.id)));
    }
  };

  const [exporting, setExporting] = useState(false);

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const response = await alertsAPI.exportAlerts({
        siteId: selectedSite?.id,
        severity: filters.severity !== 'ALL' ? filters.severity : undefined,
        status: filters.status !== 'ALL' ? filters.status : undefined,
        cameraId: filters.cameraId || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        tz: getActiveTimezoneInfo().timezone,
        format,
      });
      const mimeType = format === 'csv' ? 'text/csv' : 'application/pdf';
      const ext = format === 'csv' ? 'csv' : 'pdf';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `alerts-${new Date().toISOString().split('T')[0]}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Alerts exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Failed to export alerts');
    } finally {
      setExporting(false);
    }
  };

  const alerts = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  // Resolve a pending cross-page lightbox jump once the target page's data has
  // actually loaded. Guarding on pagination.page === filters.page (plus not
  // fetching) ensures we open the new page's edge alert, never a stale one.
  useEffect(() => {
    if (!pendingEdgeNav) return;
    if (isFetching) return;
    if (pagination.page !== filters.page) return; // target page not loaded yet
    if (alerts.length === 0) { setPendingEdgeNav(null); return; }
    setViewingAlert(pendingEdgeNav === 'first' ? alerts[0] : alerts[alerts.length - 1]);
    setPendingEdgeNav(null);
  }, [pendingEdgeNav, isFetching, pagination.page, filters.page, alerts]);

  const applyQuickFilter = (quickFilter) => {
    setFilters(prev => ({ ...prev, ...quickFilter.values, page: 1 }));
    setSelectedIds(new Set());
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="page-title">Safety Alerts</h1>
            <TimezonePill />
          </div>
          <p className="page-description">Triage active safety events by severity, camera, and zone</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(v => !v)}
              disabled={exporting}
              className="btn btn-secondary flex items-center gap-2"
              aria-expanded={showExportMenu}
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export'}</span>
              <ChevronDownIcon className="w-4 h-4" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 z-40 mt-2 w-40 rounded-lg border border-slate-200 bg-white shadow-lg p-1">
                <button
                  onClick={() => {
                    setShowExportMenu(false);
                    handleExport('csv');
                  }}
                  className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-50 text-slate-700"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => {
                    setShowExportMenu(false);
                    handleExport('pdf');
                  }}
                  className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-slate-50 text-slate-700"
                >
                  Export PDF
                </button>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowResolveAllConfirm(true)}
            disabled={resolveAllMutation.isPending}
            className="btn btn-secondary flex items-center gap-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
            title="Resolve every open alert for the current site"
          >
            <CheckCircleIcon className="w-4 h-4" />
            <span className="hidden sm:inline">
              {resolveAllMutation.isPending ? 'Resolving…' : 'Resolve All'}
            </span>
          </button>
          <button
            onClick={() => refetch()}
            className="btn btn-secondary flex items-center gap-2"
          >
            <ArrowPathIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Resolve-all confirmation */}
      {showResolveAllConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="p-5 border-b">
              <h2 className="text-lg font-semibold text-slate-900">Resolve all active alerts?</h2>
              <p className="mt-1 text-sm text-slate-500">
                {selectedSite?.name
                  ? <>Every open alert for <strong>{selectedSite.name}</strong> (active, acknowledged, or escalated) will be marked resolved. This can't be undone in bulk — individual alerts can still be re-opened later if needed.</>
                  : <>Every open alert across all your sites (active, acknowledged, or escalated) will be marked resolved.</>}
              </p>
            </div>
            <div className="p-5 flex justify-end gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => setShowResolveAllConfirm(false)}
                disabled={resolveAllMutation.isPending}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary bg-emerald-600 hover:bg-emerald-700"
                onClick={() => { setShowResolveAllConfirm(false); resolveAllMutation.mutate(); }}
                disabled={resolveAllMutation.isPending}
              >
                {resolveAllMutation.isPending ? 'Resolving…' : 'Resolve all'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Matches"
          value={pagination.total || 0}
          color="blue"
          delay={1}
        />
        <StatCard
          label="Critical Shown"
          value={alerts.filter(a => a.severity === 'CRITICAL').length}
          color="red"
          delay={2}
        />
        <StatCard
          label="Active Shown"
          value={alerts.filter(a => a.status === 'ACTIVE').length}
          color="orange"
          delay={3}
        />
        <StatCard
          label="Resolved Shown"
          value={alerts.filter(a => a.status === 'RESOLVED').length}
          color="green"
          delay={4}
        />
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-4 items-center animate-slide-up stagger-2" style={{ opacity: 0, animationFillMode: 'forwards' }}>
        <div className="flex items-center gap-2 text-slate-500">
          <FunnelIcon className="w-5 h-5" />
          <span className="text-sm font-medium">Filters</span>
        </div>

        <div className="w-full flex flex-wrap gap-2">
          {quickFilters.map((quickFilter) => {
            const isActive = Object.entries(quickFilter.values).every(([key, value]) => filters[key] === value);
            return (
              <button
                key={quickFilter.id}
                onClick={() => applyQuickFilter(quickFilter)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors',
                  isActive
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                )}
              >
                {quickFilter.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3 flex-1">
          <FilterPills
            options={severityOptions}
            value={filters.severity}
            onChange={(v) => setFilters({ ...filters, severity: v, page: 1 })}
            label="Severity"
          />

          <div className="w-px h-8 bg-slate-200 hidden md:block"></div>

          <FilterPills
            options={statusOptions}
            value={filters.status}
            onChange={(v) => setFilters({ ...filters, status: v, page: 1 })}
            label="Status"
          />

          <div className="w-px h-8 bg-slate-200 hidden md:block"></div>

          <FilterPills
            options={categoryOptions}
            value={filters.category}
            onChange={(v) => setFilters({ ...filters, category: v, page: 1 })}
            label="Category"
          />
        </div>

        <span className="text-sm text-slate-500 ml-auto">
          <span className="font-semibold text-slate-800">{pagination.total || 0}</span> alerts found
        </span>

        {/* Advanced filters row */}
        <div className="w-full flex flex-wrap gap-3 items-center pt-3 border-t border-slate-100">
          {/* Location (site) filter */}
          {sites.length > 1 && (
            <select
              value={filters.siteId}
              onChange={(e) => setFilters({ ...filters, siteId: e.target.value, cameraId: '', zoneId: '', page: 1 })}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
            >
              <option value="">All Locations</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          )}

          <select
            value={filters.cameraId}
            onChange={(e) => setFilters({ ...filters, cameraId: e.target.value, page: 1 })}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
          >
            <option value="">All Cameras</option>
            {cameras.map((cam) => (
              <option key={cam.id} value={cam.id}>{cam.name}</option>
            ))}
          </select>

          <select
            value={filters.zoneId}
            onChange={(e) => setFilters({ ...filters, zoneId: e.target.value, page: 1 })}
            className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
          >
            <option value="">All Zones</option>
            {zones.map((zone) => (
              <option key={zone.id} value={zone.id}>{zone.name}</option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400">From</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-400">To</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700 focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
            />
          </div>

          {(filters.siteId || filters.cameraId || filters.zoneId || filters.startDate || filters.endDate) && (
            <button
              onClick={() => setFilters({ ...filters, siteId: '', cameraId: '', zoneId: '', startDate: '', endDate: '', page: 1 })}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="card sticky top-24 z-30 p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-blue-50 border-blue-200 animate-fade-in">
          <span className="text-sm font-semibold text-blue-800">
            {selectedIds.size} alert{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center flex-wrap gap-2">
            <button
              onClick={() => bulkAcknowledgeMutation.mutate([...selectedIds])}
              disabled={bulkAcknowledgeMutation.isPending || bulkResolveMutation.isPending || bulkFalsePositiveMutation.isPending}
              className="btn btn-primary text-sm flex items-center gap-1.5"
            >
              <CheckIcon className="w-4 h-4" />
              {bulkAcknowledgeMutation.isPending ? 'Acknowledging…' : 'Acknowledge'}
            </button>
            <button
              onClick={() => bulkResolveMutation.mutate([...selectedIds])}
              disabled={bulkAcknowledgeMutation.isPending || bulkResolveMutation.isPending || bulkFalsePositiveMutation.isPending}
              className="btn btn-secondary text-sm flex items-center gap-1.5 bg-green-600 text-white hover:bg-green-700 border-green-600"
            >
              <CheckCircleIcon className="w-4 h-4" />
              {bulkResolveMutation.isPending ? 'Resolving…' : 'Resolve'}
            </button>
            <button
              onClick={() => bulkFalsePositiveMutation.mutate([...selectedIds])}
              disabled={bulkAcknowledgeMutation.isPending || bulkResolveMutation.isPending || bulkFalsePositiveMutation.isPending}
              className="btn btn-secondary text-sm flex items-center gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
            >
              <NoSymbolIcon className="w-4 h-4" />
              {bulkFalsePositiveMutation.isPending ? 'Marking…' : 'False Positive'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="btn btn-secondary text-sm"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Alerts List */}
      <div className={clsx('card overflow-hidden animate-slide-up stagger-3 transition-opacity duration-300', isFetching && !isLoading && 'opacity-50 pointer-events-none')} style={{ animationFillMode: 'forwards' }}>
        {isLoading ? (
          <LoadingSkeleton />
        ) : alerts.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="glass-table">
                <thead>
                  <tr>
                    <th className="w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.size > 0 && selectedIds.size === alerts.filter(a => a.status === 'ACTIVE' || a.status === 'ESCALATED').length}
                        onChange={toggleSelectAll}
                        className="rounded border-slate-300"
                        aria-label="Select all alerts"
                      />
                    </th>
                    <th>Snapshot</th>
                    <th>Severity</th>
                    <th>Type</th>
                    <th>Confidence</th>
                    <th>Location</th>
                    <th>Zone</th>
                    <th>Time</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert, index) => (
                    <AlertRow
                      key={alert.id}
                      alert={alert}
                      index={index}
                      onAcknowledge={() => acknowledgeMutation.mutate(alert.id)}
                      onResolve={() => setSelectedAlert(alert)}
                      onFalsePositive={() => falsePositiveMutation.mutate({ alertId: alert.id })}
                      onViewImage={() => setViewingAlert(alert)}
                      isAcknowledging={acknowledgeMutation.isPending && acknowledgeMutation.variables === alert.id}
                      selected={selectedIds.has(alert.id)}
                      onToggleSelect={() => toggleSelect(alert.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3 p-4">
              {alerts.map((alert, index) => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  index={index}
                  onAcknowledge={() => acknowledgeMutation.mutate(alert.id)}
                  onResolve={() => setSelectedAlert(alert)}
                  onFalsePositive={() => falsePositiveMutation.mutate({ alertId: alert.id })}
                  onViewImage={() => setViewingAlert(alert)}
                  isAcknowledging={acknowledgeMutation.isPending && acknowledgeMutation.variables === alert.id}
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <nav className="flex items-center justify-between px-6 py-4 border-t border-slate-200" aria-label="Alerts pagination">
                <button
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  disabled={filters.page === 1}
                  className="btn btn-secondary text-sm flex items-center gap-1 disabled:opacity-50"
                  aria-label="Go to previous page"
                >
                  <ChevronLeftIcon className="w-4 h-4" aria-hidden="true" />
                  Previous
                </button>
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setFilters({ ...filters, page })}
                        className={clsx(
                          'w-8 h-8 rounded-lg text-sm font-medium transition-all',
                          filters.page === page
                            ? 'bg-blue-500 text-white shadow-md'
                            : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                        )}
                        aria-label={`Go to page ${page}`}
                        aria-current={filters.page === page ? 'page' : undefined}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  disabled={filters.page === pagination.pages}
                  className="btn btn-secondary text-sm flex items-center gap-1 disabled:opacity-50"
                  aria-label="Go to next page"
                >
                  Next
                  <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />
                </button>
              </nav>
            )}
          </>
        )}
      </div>

      {/* Resolve Modal */}
      {selectedAlert && (
        <ResolveModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onResolve={(resolution) => resolveMutation.mutate({
            alertId: selectedAlert.id,
            resolution
          })}
          isLoading={resolveMutation.isPending}
        />
      )}

      {/* Image Lightbox Modal */}
      {viewingAlert && (() => {
        const { primary, secondary } = formatViolations(viewingAlert);
        const subtitle = secondary ? `${primary}: ${secondary}` : primary;
        // Locate the open alert within the loaded list so the lightbox can page
        // through alerts with the next/prev arrows (and ← → keys) without closing.
        // Deep-linked alerts (fetched by id) may not be in the list → index -1
        // disables navigation, leaving a plain single-image viewer.
        const navIndex = alerts.findIndex(a => a.id === viewingAlert.id);
        const canNavigate = navIndex >= 0;
        const hasPrevPage = canNavigate && filters.page > 1;
        const hasNextPage = canNavigate && filters.page < (pagination.pages || 1);

        // Arrow / key navigation. In-page steps land in [0, len); an index of -1
        // or len means "cross into the adjacent list-page" → flip the page and
        // let the pendingEdgeNav effect open the correct edge once it loads.
        const handleNavigate = (newIndex) => {
          if (newIndex < 0) {
            if (filters.page > 1) {
              setPendingEdgeNav('last');
              setFilters(f => ({ ...f, page: f.page - 1 }));
            }
          } else if (newIndex >= alerts.length) {
            if (filters.page < (pagination.pages || 1)) {
              setPendingEdgeNav('first');
              setFilters(f => ({ ...f, page: f.page + 1 }));
            }
          } else {
            setViewingAlert(alerts[newIndex]);
          }
        };

        // After an action, jump to the next alert so reviewing is a single-click
        // loop. Tracked by id, so the optimistic status change (and the later
        // refetch that may drop the actioned alert) can't desync the position.
        const advanceAfterAction = () => {
          const idx = alerts.findIndex(a => a.id === viewingAlert.id);
          if (idx >= 0 && idx < alerts.length - 1) {
            setViewingAlert(alerts[idx + 1]);
          } else if (filters.page < (pagination.pages || 1)) {
            setPendingEdgeNav('first');
            setFilters(f => ({ ...f, page: f.page + 1 }));
          } else {
            setViewingAlert(null); // reached the end of the list
          }
        };

        const status = viewingAlert.status;
        const canAck = status === 'ACTIVE';
        const canFalsePositive = status === 'ACTIVE' || status === 'ACKNOWLEDGED';
        const canResolve = status === 'ACTIVE' || status === 'ACKNOWLEDGED';

        const lightboxActions = (
          <>
            {canAck && (
              <button
                onClick={() => { acknowledgeMutation.mutate(viewingAlert.id); advanceAfterAction(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-500/20 text-yellow-200 hover:bg-yellow-500/30 transition-colors text-sm font-medium"
                title="Acknowledge (then next)"
              >
                <CheckIcon className="w-4 h-4" /> Acknowledge
              </button>
            )}
            {canFalsePositive && (
              <button
                onClick={() => { falsePositiveMutation.mutate({ alertId: viewingAlert.id }); advanceAfterAction(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 text-white/80 hover:bg-white/20 transition-colors text-sm font-medium"
                title="Mark false positive (then next)"
              >
                <NoSymbolIcon className="w-4 h-4" /> False Positive
              </button>
            )}
            {canResolve && (
              <button
                onClick={() => { resolveMutation.mutate({ alertId: viewingAlert.id, resolution: 'Resolved during alert review.' }); advanceAfterAction(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/30 text-emerald-200 hover:bg-emerald-500/40 transition-colors text-sm font-medium"
                title="Resolve (then next)"
              >
                <CheckCircleIcon className="w-4 h-4" /> Resolve
              </button>
            )}
          </>
        );

        // List-wide counter ("47 / 312") rather than position within the page.
        const globalPos = canNavigate
          ? (filters.page - 1) * (pagination.limit || alerts.length) + navIndex + 1
          : 0;
        const counterText = canNavigate && pagination.total
          ? `${globalPos} / ${pagination.total}`
          : undefined;

        return (
          <ImageModal
            isOpen={!!viewingAlert}
            onClose={() => setViewingAlert(null)}
            imageUrl={getAlertImageUrl(viewingAlert, true)}
            fallbackUrl={getThumbnailUrl(viewingAlert)}
            title={`${viewingAlert.type?.replace(/_/g, ' ')} - ${viewingAlert.severity}`}
            subtitle={subtitle}
            images={canNavigate ? alerts : []}
            currentIndex={canNavigate ? navIndex : 0}
            onNavigate={canNavigate ? handleNavigate : undefined}
            hasPrevPage={hasPrevPage}
            hasNextPage={hasNextPage}
            counterText={counterText}
            actions={canAck || canFalsePositive || canResolve ? lightboxActions : undefined}
            metadata={{
              confidence: viewingAlert.confidence,
              detectedAt: viewingAlert.detectedAt,
              camera: viewingAlert.camera?.name,
              type: viewingAlert.type,
              violations: viewingAlert.metadata?.violations || viewingAlert.violations,
            }}
          />
        );
      })()}
    </div>
  );
}

function StatCard({ label, value, color, delay }) {
  const colorConfig = {
    blue: { bg: 'bg-blue-100', dot: 'bg-blue-500' },
    red: { bg: 'bg-red-100', dot: 'bg-red-500' },
    orange: { bg: 'bg-orange-100', dot: 'bg-orange-500' },
    green: { bg: 'bg-emerald-100', dot: 'bg-emerald-500' },
  };

  const config = colorConfig[color];

  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <span className="stat-card-label">{label}</span>
        <div className={clsx('w-2 h-2 rounded-full', config.dot)}></div>
      </div>
      <p className="stat-card-value">{value}</p>
    </div>
  );
}

function FilterPills({ options, value, onChange, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-400 hidden sm:block">{label}:</span>
      <div className="flex gap-1">
        {options.slice(0, 5).map((opt) => (
          <button
            key={opt}
            onClick={() => onChange(opt)}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
              value === opt
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
            )}
          >
            {opt === 'ALL' ? 'All' : opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="p-6 space-y-4">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-16 skeleton rounded-xl"></div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="empty-state py-16">
      <div className="empty-state-icon !bg-emerald-100">
        <ShieldCheckIcon className="w-10 h-10 text-emerald-600" />
      </div>
      <h3 className="empty-state-title">No alerts found</h3>
      <p className="empty-state-description">All systems are operating normally. Great job keeping the site safe!</p>
    </div>
  );
}

// Virtualized table for desktop - handles thousands of rows efficiently
function VirtualizedAlertTable({ alerts, onAcknowledge, onResolve, onViewImage, acknowledgingId }) {
  const parentRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: alerts.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72, // Estimated row height in px
    overscan: 5, // Render 5 extra rows above/below viewport
  });

  const severityConfig = {
    CRITICAL: { badge: 'badge-critical', icon: FireIcon },
    HIGH: { badge: 'badge-high', icon: ExclamationTriangleIcon },
    MEDIUM: { badge: 'badge-medium', icon: ExclamationTriangleIcon },
    LOW: { badge: 'badge-low', icon: BellAlertIcon },
  };

  const statusConfig = {
    ACTIVE: 'bg-red-500/20 text-red-300 border-red-500/30',
    ACKNOWLEDGED: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    RESOLVED: 'bg-green-500/20 text-green-300 border-green-500/30',
    FALSE_POSITIVE: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  };

  return (
    <div className="hidden md:block overflow-x-auto">
      {/* Sticky header */}
      <table className="glass-table w-full">
        <thead className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm">
          <tr>
            <th className="w-24">Snapshot</th>
            <th className="w-24">Severity</th>
            <th>Type</th>
            <th>Location</th>
            <th>Zone</th>
            <th className="w-40">Time</th>
            <th className="w-32">Status</th>
            <th className="w-32 text-right">Actions</th>
          </tr>
        </thead>
      </table>

      {/* Virtualized body */}
      <div
        ref={parentRef}
        className="max-h-[600px] overflow-auto"
        style={{ contain: 'strict' }}
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          <table className="glass-table w-full" style={{ tableLayout: 'fixed' }}>
            <tbody>
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const alert = alerts[virtualRow.index];
                const config = severityConfig[alert.severity] || severityConfig.LOW;
                const isAcknowledging = acknowledgingId === alert.id;

                return (
                  <tr
                    key={alert.id}
                    className={clsx(
                      alert.severity === 'CRITICAL' && alert.status === 'ACTIVE' && 'bg-red-500/5'
                    )}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <td className="w-24">
                      <AlertThumbnail
                        imageUrl={getThumbnailUrl(alert)}
                        alt={`${alert.type} snapshot`}
                        size="md"
                        onClick={() => onViewImage(alert)}
                        showPlaceholder={true}
                      />
                    </td>
                    <td className="w-24">
                      <span className={clsx('badge', config.badge)}>
                        {alert.severity}
                      </span>
                    </td>
                    <td>
                      <p className="font-medium text-slate-800">{alert.type?.replace(/_/g, ' ')}</p>
                      {(() => {
                        const { primary, secondary } = formatViolations(alert);
                        return (
                          <>
                            <p className="text-sm text-slate-500">{primary}</p>
                            {secondary && (
                              <p className="text-xs text-slate-400 truncate max-w-48" title={secondary}>
                                {secondary}
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      <p className="text-slate-800">{alert.site?.name}</p>
                      <p className="text-sm text-slate-500">{alert.camera?.name}</p>
                    </td>
                    <td>
                      <p className="text-slate-800">
                        {alert.zone?.name || alert.metadata?.zoneName || <span className="text-slate-400">—</span>}
                      </p>
                    </td>
                    <td className="w-40 text-slate-500 text-sm">
                      {formatDateTime(alert.detectedAt)}
                    </td>
                    <td className="w-32">
                      <span className={clsx('badge border', statusConfig[alert.status])}>
                        {alert.status}
                      </span>
                    </td>
                    <td className="w-32 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {alert.status === 'ACTIVE' && (
                          <button
                            onClick={() => onAcknowledge(alert.id)}
                            disabled={isAcknowledging}
                            className={clsx(
                              "p-2 rounded-lg transition-all",
                              isAcknowledging
                                ? "bg-yellow-500/10 text-yellow-300/50 cursor-not-allowed"
                                : "bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30"
                            )}
                            title="Acknowledge"
                          >
                            {isAcknowledging ? (
                              <div className="w-4 h-4 border-2 border-yellow-300/30 border-t-yellow-300 rounded-full animate-spin" />
                            ) : (
                              <CheckIcon className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        {(alert.status === 'ACTIVE' || alert.status === 'ACKNOWLEDGED') && (
                          <button
                            onClick={() => onResolve(alert)}
                            className="btn btn-success text-sm py-1.5 px-3"
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Virtualized cards for mobile
function VirtualizedAlertCards({ alerts, onAcknowledge, onResolve, onViewImage, acknowledgingId }) {
  const parentRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: alerts.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 140, // Estimated card height
    overscan: 3,
  });

  const severityConfig = {
    CRITICAL: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-300' },
    HIGH: { bg: 'bg-orange-500/20', border: 'border-orange-500/50', text: 'text-orange-300' },
    MEDIUM: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/50', text: 'text-yellow-300' },
    LOW: { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-300' },
  };

  return (
    <div
      ref={parentRef}
      className="md:hidden max-h-[70vh] overflow-auto p-4"
      style={{ contain: 'strict' }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const alert = alerts[virtualRow.index];
          const config = severityConfig[alert.severity] || severityConfig.LOW;
          const isAcknowledging = acknowledgingId === alert.id;

          return (
            <div
              key={alert.id}
              className={clsx(
                'p-4 rounded-xl border',
                config.bg,
                config.border,
                alert.severity === 'CRITICAL' && alert.status === 'ACTIVE' && 'alert-critical'
              )}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
                marginBottom: '12px',
              }}
            >
              <div className="flex gap-4">
                <AlertThumbnail
                  imageUrl={getThumbnailUrl(alert)}
                  alt={`${alert.type} snapshot`}
                  size="lg"
                  onClick={() => onViewImage(alert)}
                  showPlaceholder={false}
                  className="flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={clsx('font-semibold', config.text)}>
                        {alert.type?.replace(/_/g, ' ')}
                      </p>
                      {(() => {
                        const { primary, secondary } = formatViolations(alert);
                        return (
                          <>
                            <p className="text-sm text-slate-500">{primary}</p>
                            {secondary && (
                              <p className="text-xs text-slate-400 truncate max-w-32" title={secondary}>
                                {secondary}
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <span className={clsx('badge', config.text, config.bg, config.border)}>
                      {alert.severity}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-slate-400">
                      {formatDateTime(alert.detectedAt)}
                    </span>
                    <div className="flex gap-2">
                      {alert.status === 'ACTIVE' && (
                        <button
                          onClick={() => onAcknowledge(alert.id)}
                          disabled={isAcknowledging}
                          className={clsx(
                            "p-1.5 rounded-lg transition-all",
                            isAcknowledging
                              ? "bg-yellow-500/10 text-yellow-300/50 cursor-not-allowed"
                              : "bg-yellow-500/20 text-yellow-300"
                          )}
                        >
                          {isAcknowledging ? (
                            <div className="w-4 h-4 border-2 border-yellow-300/30 border-t-yellow-300 rounded-full animate-spin" />
                          ) : (
                            <CheckIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      {(alert.status === 'ACTIVE' || alert.status === 'ACKNOWLEDGED') && (
                        <button onClick={() => onResolve(alert)} className="btn btn-success text-xs py-1 px-2">
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Download an alert's snapshot directly (no need to open the lightbox first).
// Fetches the image as a blob so the browser saves it with a sensible filename
// even when the image is served from a different origin (the `download` attribute
// alone is ignored cross-origin). Falls back to opening the image in a new tab.
async function downloadAlertSnapshot(alert) {
  const url = getImageUrl(getAlertImageUrl(alert));
  if (!url) {
    toast.error('No snapshot available for this alert');
    return;
  }
  const stamp = (alert.detectedAt ? new Date(alert.detectedAt) : new Date())
    .toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const type = (alert.type || 'alert').replace(/_/g, '-').toLowerCase();
  const filename = `${type}-${stamp}.jpg`;
  try {
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    // Cross-origin/CORS or network failure — fall back to opening the image.
    window.open(url, '_blank', 'noopener');
  }
}

function AlertRow({ alert, index, onAcknowledge, onResolve, onFalsePositive, onViewImage, isAcknowledging, selected, onToggleSelect }) {
  const severityConfig = {
    CRITICAL: { badge: 'badge-critical', icon: FireIcon },
    HIGH: { badge: 'badge-high', icon: ExclamationTriangleIcon },
    MEDIUM: { badge: 'badge-medium', icon: ExclamationTriangleIcon },
    LOW: { badge: 'badge-low', icon: BellAlertIcon },
  };

  const statusConfig = {
    ACTIVE: 'bg-red-500/20 text-red-300 border-red-500/30',
    ACKNOWLEDGED: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    RESOLVED: 'bg-green-500/20 text-green-300 border-green-500/30',
    FALSE_POSITIVE: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  };

  const config = severityConfig[alert.severity] || severityConfig.LOW;

  return (
    <tr
      className={clsx(
        'animate-fade-in',
        alert.severity === 'CRITICAL' && alert.status === 'ACTIVE' && 'bg-red-500/5'
      )}
      style={{ animationDelay: `${index * 0.05}s`, opacity: 0, animationFillMode: 'forwards' }}
    >
      <td>
        {(alert.status === 'ACTIVE' || alert.status === 'ESCALATED') ? (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            className="rounded border-slate-300"
            aria-label={`Select alert: ${alert.type?.replace(/_/g, ' ')}`}
          />
        ) : <span className="w-4" />}
      </td>
      <td>
        <div className="flex items-center gap-2">
          <AlertThumbnail
            imageUrl={getThumbnailUrl(alert)}
            alt={`${alert.type} snapshot`}
            size="md"
            onClick={onViewImage}
            showPlaceholder={true}
          />
          <button
            onClick={() => downloadAlertSnapshot(alert)}
            className="p-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-blue-500/20 hover:text-blue-600 transition-all"
            title="Download snapshot"
            aria-label="Download this alert's snapshot"
          >
            <ArrowDownTrayIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </td>
      <td>
        <span className={clsx('badge', config.badge)}>
          {alert.severity}
        </span>
      </td>
      <td>
        <p className="font-medium text-slate-800">{alert.type?.replace(/_/g, ' ')}</p>
        {(() => {
          const { primary, secondary } = formatViolations(alert);
          return (
            <>
              <p className="text-sm text-slate-500">{primary}</p>
              {secondary && (
                <p className="text-xs text-slate-400 truncate max-w-48" title={secondary}>
                  {secondary}
                </p>
              )}
            </>
          );
        })()}
      </td>
      <td>
        <span className="text-sm font-semibold text-slate-700">
          {formatConfidence(alert)}
        </span>
      </td>
      <td>
        <p className="text-slate-800">{alert.site?.name}</p>
        <p className="text-sm text-slate-500">{alert.camera?.name}</p>
      </td>
      <td>
        <p className="text-slate-800">
          {alert.zone?.name || alert.metadata?.zoneName || <span className="text-slate-400">—</span>}
        </p>
      </td>
      <td className="text-slate-500 text-sm">
        {formatDateTime(alert.detectedAt)}
      </td>
      <td>
        <span className={clsx('badge border', statusConfig[alert.status])}>
          {alert.status}
        </span>
      </td>
      <td className="text-right">
        <div className="flex items-center justify-end gap-2">
          {alert.status === 'ACTIVE' && (
            <button
              onClick={onAcknowledge}
              disabled={isAcknowledging}
              className={clsx(
                "p-2 rounded-lg transition-all",
                isAcknowledging
                  ? "bg-yellow-500/10 text-yellow-300/50 cursor-not-allowed"
                  : "bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30"
              )}
              title="Acknowledge"
              aria-label="Acknowledge this alert"
            >
              {isAcknowledging ? (
                <div className="w-4 h-4 border-2 border-yellow-300/30 border-t-yellow-300 rounded-full animate-spin" aria-label="Acknowledging..." />
              ) : (
                <CheckIcon className="w-4 h-4" aria-hidden="true" />
              )}
            </button>
          )}
          {(alert.status === 'ACTIVE' || alert.status === 'ACKNOWLEDGED') && (
            <button
              onClick={onFalsePositive}
              className="p-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-all"
              title="Mark as false positive"
              aria-label="Mark this alert as a false positive"
            >
              <NoSymbolIcon className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
          {(alert.status === 'ACTIVE' || alert.status === 'ACKNOWLEDGED') && (
            <button
              onClick={onResolve}
              className="btn btn-success text-sm py-1.5 px-3"
            >
              Resolve
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function AlertCard({ alert, index, onAcknowledge, onResolve, onFalsePositive, onViewImage, isAcknowledging }) {
  const severityConfig = {
    CRITICAL: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-300' },
    HIGH: { bg: 'bg-orange-500/20', border: 'border-orange-500/50', text: 'text-orange-300' },
    MEDIUM: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/50', text: 'text-yellow-300' },
    LOW: { bg: 'bg-blue-500/20', border: 'border-blue-500/50', text: 'text-blue-300' },
  };

  const config = severityConfig[alert.severity] || severityConfig.LOW;

  return (
    <div
      className={clsx(
        'p-4 rounded-xl border animate-fade-in',
        config.bg,
        config.border,
        alert.severity === 'CRITICAL' && alert.status === 'ACTIVE' && 'alert-critical'
      )}
      style={{ animationDelay: `${index * 0.05}s`, opacity: 0, animationFillMode: 'forwards' }}
    >
      <div className="flex gap-4">
        <div className="flex flex-col items-center gap-2 flex-shrink-0">
          <AlertThumbnail
            imageUrl={getThumbnailUrl(alert)}
            alt={`${alert.type} snapshot`}
            size="lg"
            onClick={onViewImage}
            showPlaceholder={false}
          />
          <button
            onClick={() => downloadAlertSnapshot(alert)}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-slate-500 hover:bg-blue-500/20 hover:text-blue-600 transition-all text-xs"
            title="Download snapshot"
            aria-label="Download this alert's snapshot"
          >
            <ArrowDownTrayIcon className="w-4 h-4" aria-hidden="true" />
            Download
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className={clsx('font-semibold', config.text)}>
                {alert.type?.replace(/_/g, ' ')}
              </p>
              {(() => {
                const { primary, secondary } = formatViolations(alert);
                return (
                  <>
                    <p className="text-sm text-slate-500">{primary}</p>
                    {secondary && (
                      <p className="text-xs text-slate-400 truncate max-w-32" title={secondary}>
                        {secondary}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
            <span className={clsx('badge', config.text, config.bg, config.border)}>
              {alert.severity}
            </span>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-slate-400">
              <span>{formatDateTime(alert.detectedAt)}</span>
              <span className="mx-1.5">-</span>
              <span>{formatConfidence(alert)} confidence</span>
            </div>
            <div className="flex gap-2">
              {alert.status === 'ACTIVE' && (
                <button
                  onClick={onAcknowledge}
                  disabled={isAcknowledging}
                  className={clsx(
                    "p-1.5 rounded-lg transition-all",
                    isAcknowledging
                      ? "bg-yellow-500/10 text-yellow-300/50 cursor-not-allowed"
                      : "bg-yellow-500/20 text-yellow-300"
                  )}
                >
                  {isAcknowledging ? (
                    <div className="w-4 h-4 border-2 border-yellow-300/30 border-t-yellow-300 rounded-full animate-spin" />
                  ) : (
                    <CheckIcon className="w-4 h-4" />
                  )}
                </button>
              )}
              {(alert.status === 'ACTIVE' || alert.status === 'ACKNOWLEDGED') && (
                <button
                  onClick={onFalsePositive}
                  className="p-1.5 rounded-lg bg-slate-700/50 text-slate-400 hover:bg-slate-700 transition-all"
                  title="Mark as false positive"
                >
                  <NoSymbolIcon className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
              {(alert.status === 'ACTIVE' || alert.status === 'ACKNOWLEDGED') && (
                <button onClick={onResolve} className="btn btn-success text-xs py-1 px-2">
                  Resolve
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const RESOLUTION_PRESETS = [
  { label: 'Issue corrected', value: 'Issue identified and corrected on-site.' },
  { label: 'False positive', value: 'Alert reviewed — determined to be a false positive.' },
  { label: 'Worker briefed', value: 'Worker briefed on safety requirements; compliance restored.' },
  { label: 'No action needed', value: 'Reviewed with supervisor; no further action required.' },
];

const SEVERITY_BADGE = {
  CRITICAL: 'bg-red-100 text-red-700 border border-red-200',
  HIGH: 'bg-orange-100 text-orange-700 border border-orange-200',
  MEDIUM: 'bg-amber-100 text-amber-700 border border-amber-200',
  LOW: 'bg-blue-100 text-blue-700 border border-blue-200',
};

function ResolveModal({ alert, onClose, onResolve, isLoading }) {
  const [resolution, setResolution] = useState('');
  const [imgError, setImgError] = useState(false);

  const snapshotSrc = alert.snapshotUrl
    ? (alert.snapshotUrl.startsWith('http') ? alert.snapshotUrl : `${API_URL}${alert.snapshotUrl}`)
    : null;

  const zoneName = alert.zone?.name || alert.metadata?.zoneName || null;
  const confidence = typeof alert.confidence === 'number' ? Math.round(alert.confidence * 100) : null;
  const description = alert.metadata?.description || null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-labelledby="resolve-modal-title" onClick={onClose}>
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title" id="resolve-modal-title">Resolve Alert</h3>
            <button onClick={onClose} className="icon-btn" aria-label="Close dialog">
              <XMarkIcon className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>

          <div className="modal-body space-y-4">
            {/* Snapshot preview */}
            {snapshotSrc && !imgError && (
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-900 max-h-48 flex items-center justify-center">
                <img
                  src={snapshotSrc}
                  alt="Alert snapshot"
                  className="max-h-48 w-full object-contain"
                  onError={() => setImgError(true)}
                />
              </div>
            )}

            {/* Alert summary card */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="font-semibold text-slate-800 text-sm">{alert.type?.replace(/_/g, ' ')}</p>
                <div className="flex items-center gap-2">
                  {alert.severity && (
                    <span className={clsx('text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide', SEVERITY_BADGE[alert.severity] || 'bg-slate-100 text-slate-600')}>
                      {alert.severity}
                    </span>
                  )}
                  {alert.status && (
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wide">
                      {alert.status}
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
                {alert.camera?.name && (
                  <span><span className="font-medium text-slate-600">Camera:</span> {alert.camera.name}</span>
                )}
                {zoneName && (
                  <span><span className="font-medium text-slate-600">Zone:</span> {zoneName}</span>
                )}
                {confidence !== null && (
                  <span><span className="font-medium text-slate-600">Confidence:</span> {confidence}%</span>
                )}
                {alert.workerName && (
                  <span><span className="font-medium text-slate-600">Worker:</span> {alert.workerName}</span>
                )}
              </div>

              {description && (
                <p className="text-xs text-slate-500 italic pt-1 border-t border-slate-200">{description}</p>
              )}
            </div>

            {/* Quick-pick presets */}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1.5">Quick resolution</p>
              <div className="flex flex-wrap gap-1.5">
                {RESOLUTION_PRESETS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setResolution(p.value)}
                    className={clsx(
                      'text-xs px-2.5 py-1 rounded-lg border transition-colors',
                      resolution === p.value
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400 hover:text-emerald-700'
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="Enter resolution notes (required)…"
              className="input h-24 resize-none"
              aria-label="Resolution notes"
            />
          </div>

          <div className="modal-footer">
            <button onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => onResolve(resolution)}
              disabled={!resolution.trim() || isLoading}
              className="btn btn-success disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Resolving...
                </span>
              ) : 'Resolve Alert'}
            </button>
          </div>
      </div>
    </div>
  );
}
